import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import type { QixitAuthApi, QixitAuthAttribution, QixitAuthContext } from './types';
import { errorMessage } from './types';

export type ExpressSignupSuccess = {
  accessToken: string;
  refreshToken?: string;
  userId: string;
  username: string;
  isProvisional: boolean;
};

type SignupPolicy = {
  expressAuthMode?: string;
  expressEnabled?: boolean;
  termsDocumentVersion?: string;
};

type ExpressResponse = {
  success?: boolean;
  message?: string;
  mode?: string;
  accessToken?: string;
  refreshToken?: string;
  userId?: string;
  username?: string;
  isProvisional?: boolean;
  existingAccount?: boolean;
  blockedReason?: string;
  codeSent?: boolean;
  requiresCode?: boolean;
  magicLinkSent?: boolean;
  requiresMagicLink?: boolean;
  accounts?: Array<{ accountRef: string; accountTypeLabel: string; accountType: string }>;
};

type Props = {
  api: QixitAuthApi;
  context: QixitAuthContext;
  attribution: QixitAuthAttribution;
  returnUrl?: string;
  /** When set, the email field is hidden and this address is created. */
  lockedEmail?: string;
  /** Shown as a link when express signup is switched off. */
  fullRegisterUrl?: string;
  onExpressDisabled?: () => void;
  onSuccess: (result: ExpressSignupSuccess) => void | Promise<void>;
  onCancel?: () => void;
  compact?: boolean;
};

export default function ExpressSignup({
  api,
  context,
  attribution,
  returnUrl,
  lockedEmail,
  fullRegisterUrl,
  onExpressDisabled,
  onSuccess,
  onCancel,
  compact = true,
}: Props) {
  const [policy, setPolicy] = useState<SignupPolicy | null>(null);
  const [email, setEmail] = useState(lockedEmail || '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [waitedSeconds, setWaitedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [requiresCode, setRequiresCode] = useState(false);
  const [requiresMagicLink, setRequiresMagicLink] = useState(false);
  const [existingChoices, setExistingChoices] = useState<ExpressResponse['accounts']>([]);

  const fontSize = compact ? '11px' : '14px';
  const smallFont = compact ? '10px' : '12px';
  const inputPad = compact ? '6px' : '10px';

  useEffect(() => {
    api
      .get<SignupPolicy>('/api/Registration/signup-policy', { deviceId: api.getDeviceId() })
      .then((data) => setPolicy(data || {}))
      .catch(() => setPolicy({ expressEnabled: false, expressAuthMode: 'disabled' }));
  }, [api]);

  const expressEnabled =
    policy?.expressEnabled === true ||
    (!!policy?.expressAuthMode && policy.expressAuthMode !== 'disabled');

  useEffect(() => {
    if (policy && !expressEnabled) onExpressDisabled?.();
  }, [policy, expressEnabled, onExpressDisabled]);

  async function submit(extra?: { code?: string }) {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const referralCode = (attribution.referralCode || '').trim();
      const data = await api.post<ExpressResponse>('/api/Registration/express-signup', {
        email: email.trim(),
        acceptTerms: true,
        checkoutContext: context,
        returnUrl: returnUrl || window.location.href,
        referralCode: referralCode || undefined,
        // Express signup credits the vendor as tier 1 from these ids; dropping them
        // sends a no-code signup to the default referrer instead.
        payoutId: attribution.payoutId || undefined,
        productId: attribution.productId || undefined,
        deviceId: api.getDeviceId(),
        sourcePage: attribution.sourcePage || document.referrer || undefined,
        code: extra?.code || code.trim() || undefined,
      });

      if (data.existingAccount) {
        setExistingChoices(data.accounts || []);
        setInfo(data.message || 'This email already has an account. Sign in to continue.');
        return;
      }
      if (data.requiresCode || data.codeSent) {
        setRequiresCode(true);
        setInfo(data.message || 'Enter the 6-digit code we emailed you.');
        return;
      }
      if (data.requiresMagicLink || data.magicLinkSent) {
        setRequiresMagicLink(true);
        setInfo(data.message || 'Check your email for a sign-in link.');
        return;
      }
      if (!data.success || !data.accessToken || !data.userId || !data.username) {
        setError(data.message || data.blockedReason || 'Signup failed');
        return;
      }
      // Stay on "creating" until the host finishes signing in and navigates away.
      await onSuccess({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        userId: data.userId,
        username: data.username,
        isProvisional: !!data.isProvisional,
      });
    } catch (e: unknown) {
      setError(errorMessage(e, 'Signup failed'));
    } finally {
      setBusy(false);
    }
  }

  // Creating the account is the slow step (about 10–15 seconds). A ticking count
  // shows the page is still working until the host opens Mailbox.
  const creatingAccount = busy && !requiresCode;
  useEffect(() => {
    if (!creatingAccount) {
      setWaitedSeconds(0);
      return;
    }
    const timer = window.setInterval(() => setWaitedSeconds((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [creatingAccount]);

  if (policy === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }} data-testid="express-signup-loading">
        <CircularProgress size={18} />
      </Box>
    );
  }

  if (!expressEnabled) {
    return (
      <Box sx={{ width: '100%' }} data-testid="express-signup-disabled">
        {fullRegisterUrl ? (
          <Link href={fullRegisterUrl} sx={{ fontSize }} data-testid="express-create-account">
            Create Account
          </Link>
        ) : (
          <Typography sx={{ fontSize }}>Email signup is unavailable right now.</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="express-signup">
      {!lockedEmail && (
        <TextField
          label="Email"
          type="email"
          fullWidth
          size="small"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy || requiresMagicLink}
          sx={{ mb: 0.5 }}
          inputProps={{ style: { fontSize, padding: inputPad }, 'data-testid': 'express-email' }}
          InputLabelProps={{ style: { fontSize } }}
        />
      )}
      {lockedEmail && (
        <Typography sx={{ fontSize, mb: 0.5 }} data-testid="express-title">
          Creating an account for <strong>{lockedEmail}</strong>
        </Typography>
      )}
      {requiresCode && (
        <TextField
          label="6-digit code"
          fullWidth
          size="small"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={busy}
          sx={{ mb: 0.5 }}
          inputProps={{ style: { fontSize, padding: inputPad }, maxLength: 6, 'data-testid': 'express-code' }}
          InputLabelProps={{ style: { fontSize } }}
        />
      )}
      {error && (
        <Typography color="error" sx={{ fontSize: smallFont, mb: 0.5 }} data-testid="express-error">
          {error}
        </Typography>
      )}
      {info && (
        <Typography color="text.secondary" sx={{ fontSize: smallFont, mb: 0.5 }} data-testid="express-info">
          {info}
        </Typography>
      )}
      {existingChoices && existingChoices.length > 0 && (
        <Typography sx={{ fontSize: smallFont, mb: 0.5 }} data-testid="express-existing-account">
          Sign in to your {existingChoices.map((a) => a.accountTypeLabel).join(' or ')} account.
        </Typography>
      )}
      {creatingAccount && (
        <Box
          role="status"
          data-testid="express-creating"
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
        >
          <CircularProgress size={18} sx={{ mt: '2px', flexShrink: 0 }} />
          <Typography sx={{ fontSize, color: 'text.secondary' }}>
            Creating your account and signing you in. This can take a short time
            {waitedSeconds > 0 ? ` (${waitedSeconds}s)` : ''}.
          </Typography>
        </Box>
      )}
      {!requiresMagicLink && (
        <Button
          variant="contained"
          fullWidth
          size="small"
          disabled={busy || !email.trim() || (requiresCode && code.trim().length < 4)}
          onClick={() => submit(requiresCode ? { code } : undefined)}
          sx={{ mb: 0.5, fontSize, padding: compact ? '4px' : undefined }}
          data-testid="express-continue"
        >
          {creatingAccount ? 'Creating your account…' : busy ? 'Please wait…' : requiresCode ? 'Verify & continue' : 'Continue'}
        </Button>
      )}
      {onCancel && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Link component="button" type="button" onClick={onCancel} sx={{ fontSize: smallFont }} data-testid="express-cancel">
            Back
          </Link>
        </Box>
      )}
    </Box>
  );
}
